<template>
  <div class="tab-list">
    <splide
      :key="tabs"
      class="tab-list__controls"
      :options="splideOptions"
      :has-track="false"
      role="tablist"
      :aria-labelledby="namespace"
      @splide:mounted="instance => splide = instance"
      @splide:moved="updateIndicator()"
    >
      <splide-track>
        <splide-slide
          v-for="({label}, index) of tabs"
          :key="label"
        >
          <btn
            :id="getTabControlId(index)"
            ref="tabControls"
            role="tab"
            :aria-selected="activeIndex === index"
            :aria-controls="getTabPanelId(index)"
            :tabindex="activeIndex === index ? 0 : -1"
            type="button"
            modifiers="reset"
            :label="label"
            class="tab-list__control"
            :class="{
              [$class('active')]: activeIndex === index
            }"
            @click="selectTab(index)"
            @keyup.left="selectTab(index - 1)"
            @keyup.right="selectTab(index + 1)"
          />
        </splide-slide>
      </splide-track>

      <div class="tab-list__indicator-track">
        <div
          class="tab-list__indicator"
          :style="{
            '--indicator-position': indicatorPosition,
            '--indicator-width': indicatorWidth
          }"
        />
      </div>
    </splide>

    <slot :active-index="activeIndex" />
  </div>
</template>

<script>

/**
 * Vue: Tab list (tab-list)
 * -----------------------------------------------------------------------------
 * A wrapper for tabbed content. The tablist navigation contains an underline
 * animation.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { track } from '~/helpers/datalayer-actions';
import Btn from '~global/btn/btn';

export default {
  name: 'TabList',

  components: {
    Btn,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    namespace: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    overlay: {
      type: Object,
      default: () => ({ isActive: false }),
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'active-tab': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      activeIndex: 0,
      indicatorPosition: 0,
      indicatorWidth: '0px',
      splide: null,
      splideOptions: {
        arrows: false,
        pagination: false,
        autoWidth: true,
        flickPower: 200,
        gap: 'var(--controls-gap)',
        speed: 100,
      },
    }
  },

  watch: {

    async 'overlay.isActive'() {
      await cnvs.Sleep(this.$timing('normal'))
      if (!this.overlay.isActive) {
        this.selectTab(0, false)
      }
      this.updateIndicator()
    },
  },

  methods: {

    /**
     * Get tab control ID for accessibility
     * @param {number} index
     * @returns {string}
     */
    getTabControlId(index) {
      return `${this.namespace}-tab-${index}`
    },

    /**
     * Get tab panel ID for accessibility
     * @param {number} index
     * @returns {string}
     */
    getTabPanelId(index) {
      return `${this.namespace}-tabpanel-${index}`
    },

    /**
     * Update active tab index and update overlay focusable elements
     * @param {number} newTabIndex
     * @param {boolean} [sendEvent] - Track the action?
     */
    selectTab(newTabIndex, sendEvent = true) {
      const tab = this.tabs[newTabIndex]
      let newIndex = newTabIndex

      if (newIndex < 0) {
        newIndex = this.tabs.length - 1
      } else if (newIndex === this.tabs.length) {
        newIndex = 0
      }

      this.activeIndex = newIndex

      const currentElement = this.$refs.tabControls[newIndex]?.$el

      this.$emit('active-tab', {
        tab,
        currentElement,
        index: newIndex,
      })

      if (this.overlay && this.overlay.isActive) {
        this.overlay.update({ currentElement })
      }

      if (this.splide) {
        this.splide.go(newIndex)
      }

      if (this.source && sendEvent) {
        track({
          event: `${this.source}_tab_click`,
          jh_label: tab.label,
        })
      }
    },

    /**
     * Update indicator width and position
     */
    async updateIndicator() {
      await this.$nextTick()

      if (!this.$refs.tabControls?.length) {
        return
      }

      // Update indicator position
      const gap = `calc(${this.activeIndex} * var(--controls-gap))`
      const widths = this.$refs.tabControls.slice(0, this.activeIndex)
        .reduce((accumulator, current) => {
          return accumulator + current.$el.clientWidth
        }, 0)

      const splideList = this.splide.root.querySelector('.splide__list')
      const style = window.getComputedStyle(splideList)
      const matrix = new WebKitCSSMatrix(style.transform)

      this.indicatorPosition = `calc(${gap} + ${widths}px + ${matrix.m41}px) 0`

      // Update indicator width
      console.log(this.$refs.tabControls[this.activeIndex])
      this.indicatorWidth = `${this.$refs.tabControls[this.activeIndex].$el.clientWidth}px`
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './tab-list';
</style>
